/**
 * Attribution helper shared by marketing/, town-square/, and client/.
 *
 * Captures UTM params and ad-network click IDs from incoming URLs, persists
 * them across same-origin OAuth round-trips via localStorage, and forwards
 * them across cross-domain redirects via URL params + Clerk unsafeMetadata.
 *
 * Storage shape (matches the legacy client/ format so getUtmParams keeps working):
 *   localStorage['utm_params']                  = JSON of last-touch attribution
 *   localStorage['first_visit_utm_params']      = JSON of first-touch attribution (set-once)
 *   sessionStorage['session_utm_params']        = JSON of this tab session's attribution only;
 *                                                 dies with the tab, so an upload is credited to
 *                                                 the campaign that brought THIS visit, never one
 *                                                 from weeks ago (`getSessionAttribution`)
 *   localStorage['initial_referring_user_id']   = plain string, the first share link's
 *                                                 sharer id (write-once, NOT in the blobs)
 *   localStorage['first_landing_page_url']      = plain string, the first page of the visit,
 *                                                 attribution params only (set-once)
 *   localStorage['first_referrer']              = plain string, the hostname that referred that
 *                                                 first page (set-once, written with the landing)
 *   localStorage['first_visit_browser']         = BrowserClass of that first page (set-once)
 *
 * First-visit forwarding: a cross-origin hop carries the stored landing page, referrer and
 * browser as `ah_lp` / `ah_ref` / `ah_br`, so the origin a visitor signs up on records the
 * page they actually arrived on — not its own /signup — the site that sent them, and the
 * browser they first opened it in.
 *
 * Capture rule:
 *   - Any key matching `utm_*` (open-ended; supports utm_id, utm_source_platform,
 *     utm_creative_format, utm_marketing_tactic, plus any custom utm_* keys).
 *   - Any key in KNOWN_CLICK_IDS (Meta/Google/Bing/TikTok/LinkedIn/X/Pinterest/etc.).
 *   - Keys must match KEY_PATTERN; values must be ≤ MAX_VALUE_LENGTH; total
 *     captured keys are capped at MAX_KEYS to bound localStorage growth.
 *   - The share-link `user_id` param is captured separately into its own key,
 *     write-once, only on URLs that also carry a share-link marker, and surfaced
 *     only via getReferralProperty(), never via getStoredAttribution().
 */

import { SPARKLINK_PARAM_SPARK_ID, SPARKLINK_PARAM_USER_ID } from './sparklink';
import { VISITOR_COOKIE } from './acquisition/strategy';

/**
 * Recognized non-utm_* click IDs from major ad networks.
 *
 * Keep this list focused on click IDs you want to attribute on. Adding entries
 * is cheap (purely passthrough). Anything not in this list and not prefixed
 * `utm_` is dropped at capture.
 */
const KNOWN_CLICK_IDS = [
  'fbclid', // Meta / Facebook
  'gclid', // Google Ads
  'gbraid', // Google Ads (iOS app campaigns)
  'wbraid', // Google Ads (web → app campaigns)
  'msclkid', // Microsoft / Bing Ads
  'ttclid', // TikTok Ads
  'li_fat_id', // LinkedIn Ads
  'twclid', // Twitter / X Ads
  'dclid', // Display & Video / DoubleClick
  'epik', // Pinterest Ads
  'irclid', // Impact Radius (affiliates)
] as const;

const KNOWN_CLICK_ID_SET: ReadonlySet<string> = new Set(KNOWN_CLICK_IDS);

/**
 * Attribution keys are open-ended; both raw and first-touch payloads are
 * arbitrary string maps. Consumers iterating these should iterate
 * `Object.entries`, not a fixed key list.
 */
export type RawAttribution = Record<string, string>;
export type AttributionPayload = Record<string, string>;

/** Conservative key shape: alphanumeric/underscore, 1–64 chars. */
const KEY_PATTERN = /^[a-z0-9_]{1,64}$/i;

/** Max bytes per attribution value. Real-world click IDs cap around ~100 chars. */
const MAX_VALUE_LENGTH = 256;

/** Hard ceiling on captured keys to bound localStorage / Clerk metadata size. */
const MAX_KEYS = 50;

const LAST_TOUCH_KEY = 'utm_params';
const FIRST_TOUCH_KEY = 'first_visit_utm_params';
const SESSION_TOUCH_KEY = 'session_utm_params';
const LANDING_URL_KEY = 'first_landing_page_url';
const REFERRER_KEY = 'first_referrer';
const BROWSER_KEY = 'first_visit_browser';

/** Query params a cross-origin hop forwards the first visit under. Never attribution keys. */
const FORWARDED_LANDING_PARAM = 'ah_lp';
const FORWARDED_REFERRER_PARAM = 'ah_ref';
const FORWARDED_BROWSER_PARAM = 'ah_br';

/**
 * Sites a forwarded first visit may come from: our production domains, the staging zones
 * every stage deploys under, and local dev. A forwarded `ah_lp` naming anything else is a
 * link someone else built, so it is ignored.
 */
const OWN_DOMAINS = [
  'arthelper.com',
  'arthelper.ai',
  'tryarthelper.com',
  'arthelper-dev.com',
  'artstorefrontsstaging.info',
  'localhost',
  '127.0.0.1',
];

function isOwnHost(hostname: string): boolean {
  return OWN_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
}

/** DNS caps a hostname at 253 chars; lowercase letters, digits, dots and dashes only. */
const HOSTNAME_PATTERN = /^[a-z0-9]([a-z0-9.-]{0,251}[a-z0-9])?$/;

/**
 * Where a page was opened. Meta's in-app browsers (Instagram, Facebook) keep their own
 * storage, and Google refuses OAuth inside them, so a visitor who starts there and finishes
 * signup in Safari/Chrome arrives with nothing stored. Recording the class at first visit
 * and at signup measures that handoff.
 */
export type BrowserClass = 'instagram' | 'facebook' | 'browser';

const BROWSER_CLASSES: ReadonlySet<string> = new Set(['instagram', 'facebook', 'browser']);

/** Hard ceiling on a stored/accepted landing URL. */
const MAX_URL_LENGTH = 2048;

/**
 * The query param share links carry the sharing user's asfId under. Aliased from the
 * sparklink contract rather than re-declared, so producer and consumer cannot drift.
 */
const REFERRING_USER_PARAM = SPARKLINK_PARAM_USER_ID;

/** The `utm_source` every ArtHelper share-link producer stamps. */
const SHARE_LINK_UTM_SOURCE = 'arthelper';

/**
 * Doubles as the localStorage key and the Amplitude user-property name, so
 * exported: consumers must never hardcode the string.
 *
 * Stored as a PLAIN STRING under its own key, deliberately NOT a member of the
 * `utm_params` / `first_visit_utm_params` blobs. A raw `user_id` key in the
 * last-touch blob would (a) clobber the Amplitude user property literally named
 * `user_id` (client's identify() does `identifyEvent.set('user_id', id)`),
 * (b) leak the sharer's id onto every outbound redirect via
 * appendAttributionToUrl and into Clerk unsafeMetadata via
 * readAttributionForClerk, and (c) inherit last-touch instead of first-touch
 * semantics.
 */
export const REFERRING_USER_PROPERTY = 'initial_referring_user_id';

/**
 * asfIds are uuid-or-numeric, so KEY_PATTERN is too narrow here — it has no
 * dash. Anything outside this shape is a hostile URL trying to stuff a payload
 * into a user property, so it is rejected on write and again on read.
 */
const REFERRING_USER_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;

/**
 * True if a URL/metadata key is a recognized attribution key:
 * any `utm_*` key, or one of the explicit known click IDs.
 *
 * Always validates key shape so callers can use this as a one-stop guard.
 */
export function isAttributionKey(key: string): boolean {
  if (!KEY_PATTERN.test(key)) return false;
  if (key.startsWith('utm_') && key.length > 'utm_'.length) return true;
  return KNOWN_CLICK_ID_SET.has(key);
}

/**
 * True if a value is a non-empty string within the size cap. Use this anywhere
 * you accept attribution from an untrusted source (URL, Clerk unsafeMetadata).
 */
export function isAttributionValue(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= MAX_VALUE_LENGTH;
}

/**
 * Filter an arbitrary object down to the attribution-key/value pairs that
 * pass `isAttributionKey` + `isAttributionValue`, capped at MAX_KEYS.
 *
 * Use on every untrusted boundary (URL, Clerk unsafeMetadata, page-passed objects).
 */
export function sanitizeAttribution(input: Record<string, unknown> | null | undefined): RawAttribution {
  const out: RawAttribution = {};
  if (!input || typeof input !== 'object') return out;
  let count = 0;
  for (const [key, value] of Object.entries(input)) {
    if (count >= MAX_KEYS) break;
    if (!isAttributionKey(key)) continue;
    if (!isAttributionValue(value)) continue;
    out[key] = value;
    count++;
  }
  return out;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function readJsonFromStorage(key: string, storage: () => Storage = () => localStorage): RawAttribution {
  if (!isBrowser()) return {};
  try {
    const raw = storage().getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return sanitizeAttribution(parsed);
  } catch {
    return {};
  }
}

function writeJsonToStorage(key: string, value: RawAttribution, storage: () => Storage = () => localStorage): void {
  if (!isBrowser()) return;
  try {
    storage().setItem(key, JSON.stringify(value));
  } catch {
    // localStorage may be unavailable (private mode, quota); fail silent.
  }
}

// A getter, not a reference: reading `sessionStorage` itself throws where storage is blocked,
// and that throw has to land inside the callers' try.
function sessionStorageOf(): Storage {
  return sessionStorage;
}

function readAttributionFromUrl(): RawAttribution {
  if (!isBrowser()) return {};
  const urlParams = new URLSearchParams(window.location.search);
  const out: RawAttribution = {};
  let count = 0;
  for (const [key, value] of urlParams.entries()) {
    if (count >= MAX_KEYS) break;
    if (!isAttributionKey(key)) continue;
    if (!isAttributionValue(value)) continue;
    // URLSearchParams preserves first occurrence on .get(); .entries() yields
    // every occurrence. Skip duplicates so the first-seen value wins.
    if (key in out) continue;
    out[key] = value;
    count++;
  }
  return out;
}

/**
 * True when the URL looks like an ArtHelper share link.
 *
 * `user_id` alone is far too generic to capture on: capture runs app-root-wide in
 * `client/` and layout-wide in `town-square/`, so any unrelated inbound link carrying
 * `user_id` — ESP/CRM link decoration, an admin deep link, a third-party redirect
 * forwarding its query string — would permanently stamp the visitor, because the write is
 * write-once and the property is emitted with `Identify.setOnce`.
 *
 * The risk this buys: if a future producer stops attaching `utm_source=arthelper` and has
 * no `spark_id`, capture silently stops. That is why the tests assert both live share-link
 * URL shapes by shape, not by unit.
 */
function hasShareLinkMarker(params: URLSearchParams): boolean {
  if (params.get(SPARKLINK_PARAM_SPARK_ID)) return true;
  return params.get('utm_source') === SHARE_LINK_UTM_SOURCE;
}

/**
 * Capture the share-link `user_id` from the current URL, write-once.
 *
 * Write-once is enforced per key rather than piggybacking on the blob-level
 * write-once of `first_visit_utm_params`: a visitor whose first touch was a
 * plain `gclid` landing and who clicks a share link on a later visit would
 * otherwise never get the property at all.
 *
 * Returns the id that is actually stored — pre-existing, or freshly written. Returns null
 * when there is nothing to capture AND when the write itself failed, so a caller can
 * never read success out of a capture that did not land.
 */
export function captureReferringUserFromUrl(): string | null {
  if (!isBrowser()) return null;

  const params = new URLSearchParams(window.location.search);
  if (!hasShareLinkMarker(params)) return null;

  const fromUrl = params.get(REFERRING_USER_PARAM);
  if (!fromUrl || !REFERRING_USER_PATTERN.test(fromUrl)) return null;

  const existing = getReferringUserId();
  if (existing) return existing;

  try {
    localStorage.setItem(REFERRING_USER_PROPERTY, fromUrl);
  } catch {
    // localStorage may be unavailable (private mode, quota). Still silent — a blocked
    // write is not actionable — but never reported as a stored id.
    return null;
  }
  return fromUrl;
}

/**
 * The stored referring-user id, or null when unset or unusable.
 *
 * Re-validates the value on read: localStorage is writable by any script on the
 * origin, so a stored value is still untrusted input.
 */
export function getReferringUserId(): string | null {
  if (!isBrowser()) return null;
  try {
    const stored = localStorage.getItem(REFERRING_USER_PROPERTY);
    if (!stored || !REFERRING_USER_PATTERN.test(stored)) return null;
    return stored;
  } catch {
    return null;
  }
}

/**
 * The referral user property, ready to spread — `{}` when there is nothing to emit.
 *
 * The ONLY way the referring id reaches analytics. Nothing else exports it, so
 * the self-referral rule is applied once at the source instead of being
 * re-implemented (and forgotten) per analytics call site. Pass the identifying
 * user's own id: a sharer opening their own link must not be recorded as their
 * own referrer.
 *
 * The read is IDEMPOTENT — nothing here clears the stored id, deliberately. A
 * consume-on-use read would only be safe if this layer could confirm the value was
 * transmitted, and it cannot: the client analytics implementation is a console-only stub
 * outside staging/production/mattmills, town-square's is inert without a real
 * `NEXT_PUBLIC_AMPLITUDE_API_KEY`, the SDK merely ENQUEUES an identify (the sso-callback
 * page hard-navigates cross-origin before the queue transmits), and a throwing `identify`
 * is swallowed by the caller's catch. Consuming therefore destroys real referrals on the
 * primary OAuth signup path and on every QA stage.
 *
 * Two consequences are accepted in exchange:
 *   (a) on a shared browser, a second person signing in is also stamped with the first
 *       visitor's sharer;
 *   (b) a visitor who opens their own share link keeps their own id in the write-once
 *       slot, so a later genuine referral for them is not recorded.
 *
 * Both are bounded mis-counts. Destroying real referrals on the primary signup path is
 * not — which is also why the self-referral branch below leaves the id alone: clearing it
 * there would let a sharer previewing her own link wipe the id that a genuine referral of
 * the next person to sign in on that browser depends on. Leaving it costs nothing; it is
 * re-filtered on every subsequent self identify.
 */
export function getReferralProperty(selfId?: string | number | null): AttributionPayload {
  const stored = getReferringUserId();
  if (!stored) return {};
  if (isSelfReferral(stored, selfId)) return {};
  return { [REFERRING_USER_PROPERTY]: stored };
}

function isSelfReferral(stored: string, selfId?: string | number | null): boolean {
  if (selfId === undefined || selfId === null) return false;
  // String() on both sides is required: call sites pass a GraphQL `ID` that is
  // numeric-ish (`userProfile.asfId`, a `parseInt`-ed asfId), while the stored id
  // is always a string — a bare === would silently disable this guard.
  return stored === String(selfId);
}

/**
 * Capture attribution from the current URL into localStorage.
 * Idempotent and safe to call on every page mount.
 *
 * - Merges any present URL params into `utm_params` (last-touch wins).
 * - Sets `first_visit_utm_params` only the first time any param is seen.
 * - Captures the share-link `user_id` into its own write-once key.
 *
 * Returns the raw attribution that was on the URL (may be empty). The referring
 * id is not attribution-blob data, so it never appears in that return value.
 */
export function captureAttributionFromUrl(): RawAttribution {
  // Must run before the early return below: a share link identified by `spark_id` alone
  // carries no utm_* or click-ID param, so it would hit the early return and lose the
  // referral. Referral capture gates on its own share-link marker instead.
  captureReferringUserFromUrl();
  // Also before the early return: an organic first visit carries no attribution params
  // but its landing page is still worth recording.
  captureLandingUrl();

  const fromUrl = readAttributionFromUrl();
  if (Object.keys(fromUrl).length === 0) return {};

  const existingLast = readJsonFromStorage(LAST_TOUCH_KEY);
  writeJsonToStorage(LAST_TOUCH_KEY, capKeys({ ...existingLast, ...fromUrl }));

  const existingSession = readJsonFromStorage(SESSION_TOUCH_KEY, sessionStorageOf);
  writeJsonToStorage(SESSION_TOUCH_KEY, capKeys({ ...existingSession, ...fromUrl }), sessionStorageOf);

  const existingFirst = readJsonFromStorage(FIRST_TOUCH_KEY);
  if (Object.keys(existingFirst).length === 0) {
    writeJsonToStorage(FIRST_TOUCH_KEY, fromUrl);
  }

  return fromUrl;
}

/**
 * Get just the last-touch attribution (raw keys only) from localStorage.
 */
export function getRawAttribution(): RawAttribution {
  return readJsonFromStorage(LAST_TOUCH_KEY);
}

/**
 * Attribution captured during this tab session only (raw keys only), from sessionStorage.
 * Empty for a session that arrived with no utm_* or click-ID params — a direct visit
 * credits no campaign, however recent the last one was.
 */
export function getSessionAttribution(): RawAttribution {
  return readJsonFromStorage(SESSION_TOUCH_KEY, sessionStorageOf);
}

/**
 * Get just the first-touch attribution (raw keys only) from localStorage.
 */
export function getFirstTouchAttribution(): RawAttribution {
  return readJsonFromStorage(FIRST_TOUCH_KEY);
}

/**
 * Combined object suitable for spreading: last-touch + first_*-prefixed first-touch.
 *
 * Carries utm blobs ONLY. The referring-user id is never in here: this feeds
 * marketing's Intercom contact attributes, and forwarding another user's
 * internal asfId onto a visitor's contact record is not something a caller can
 * opt out of. Call `getReferralProperty` explicitly where the referral belongs.
 */
export function getStoredAttribution(): AttributionPayload {
  const last = getRawAttribution();
  const first = getFirstTouchAttribution();
  const out: AttributionPayload = { ...last };
  for (const [key, value] of Object.entries(first)) {
    if (!value) continue;
    const firstKey = `first_${key}`;
    // Defensively re-validate the synthesized key (length check matters since
    // we're prepending `first_`). Keep within KEY_PATTERN's 64-char limit.
    if (KEY_PATTERN.test(firstKey)) {
      out[firstKey] = value;
    }
  }
  return out;
}

/**
 * Single source of truth for which attribution keys must never be overwritten
 * once set: first-touch (`first_*`) keys and the referring-user id. Analytics
 * consumers route these through their set-once primitive (Amplitude's
 * `Identify.setOnce`) instead of a plain set.
 *
 * Invariant: a `first_`-prefixed key qualifies ONLY when the remainder is itself
 * an attribution key. A naive `startsWith('first_')` is wrong because town-square's
 * `identify()` runs this over every identify user property, and it would collide
 * with the unrelated `first_name` property — routing that through `setOnce` would
 * freeze a user's first name forever, so a rename in Clerk could never reach
 * Amplitude. The client's `identify()` runs this only over the `attribution`
 * payload — its other properties (`first_name` included) are set directly via
 * hardcoded `identifyEvent.set(...)` calls above the loop and never reach this
 * predicate.
 */
export function isSetOnceAttributionKey(key: string): boolean {
  if (key === REFERRING_USER_PROPERTY) return true;
  if (!key.startsWith('first_')) return false;
  return isAttributionKey(key.slice('first_'.length));
}

/**
 * Capture URL attribution into localStorage (if any) and return the current
 * last-touch blob. Safe to call during client render / lazy state init so
 * Clerk `unsafeMetadata` is present on the first SignUp/SignIn paint.
 */
export function readAttributionForClerk(): RawAttribution {
  captureAttributionFromUrl();
  return getRawAttribution();
}

/**
 * Keep only origin + path + attribution query params (no hash). Every other param —
 * gift tokens, referral hashes, emails — is dropped.
 */
function stripUrl(url: URL): string {
  const kept = [...url.searchParams.entries()]
    .filter(([key, value]) => isAttributionKey(key) && isAttributionValue(value))
    .slice(0, MAX_KEYS);
  const search = new URLSearchParams(kept).toString();
  if (!search) {
    return `${url.origin}${url.pathname}`;
  }
  return `${url.origin}${url.pathname}?${search}`;
}

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

/**
 * Validate an untrusted landing URL: an absolute http/https URL of at most
 * MAX_URL_LENGTH chars, reduced to origin + path + attribution params. Returns
 * null otherwise. Shared by the browser (on write) and the API (on read) so both
 * sides apply one rule.
 */
export function sanitizeLandingUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > MAX_URL_LENGTH) return null;
  const url = parseUrl(value);
  if (!url) return null;
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  const stripped = stripUrl(url);
  if (stripped.length > MAX_URL_LENGTH) return null;
  return stripped;
}

/**
 * Validate an untrusted referrer — a full URL (`document.referrer`) or a bare hostname
 * (a forwarded `ah_ref`, Clerk metadata) — down to a lowercase hostname. Only the host is
 * ever kept: a referrer's path and query can carry search terms and email addresses.
 * Shared by the browser (on write) and the API (on read).
 */
export function sanitizeReferrer(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_URL_LENGTH) return null;
  const host = value.includes('://') ? parseUrl(value)?.hostname : value;
  if (!host) return null;
  const lowered = host.toLowerCase();
  if (!HOSTNAME_PATTERN.test(lowered)) return null;
  return lowered;
}

/** Validate an untrusted BrowserClass (Clerk metadata, localStorage). */
export function sanitizeBrowserClass(value: unknown): BrowserClass | null {
  if (typeof value !== 'string' || !BROWSER_CLASSES.has(value)) return null;
  return value as BrowserClass;
}

/**
 * Classify a user agent. Instagram's UA contains `Instagram`; Facebook's and Messenger's
 * carry `FBAN`/`FBAV` (iOS) or `FB_IAB` (Android).
 */
export function classifyBrowser(userAgent: string): BrowserClass {
  if (/Instagram/i.test(userAgent)) return 'instagram';
  if (/FBAN|FBAV|FB_IAB/.test(userAgent)) return 'facebook';
  return 'browser';
}

function currentBrowserClass(): BrowserClass | null {
  if (typeof navigator === 'undefined') return null;
  return classifyBrowser(navigator.userAgent ?? '');
}

/**
 * The referrer of a first visit on this page. A referrer from this same host says nothing
 * about where the visitor came from, so it is dropped.
 */
function readDocumentReferrer(): string | null {
  if (typeof document === 'undefined') return null;
  const host = sanitizeReferrer(document.referrer);
  if (!host || host === window.location.hostname.toLowerCase()) return null;
  return host;
}

type FirstVisit = { landing: string | null; referrer: string | null; browser: BrowserClass | null };

/**
 * The first visit this page represents. A forwarded `ah_lp` means the visit began elsewhere
 * — another of our origins, or another browser via a carried URL — so its landing page,
 * forwarded referrer and forwarded browser win over this page's. Never this page's
 * `document.referrer`, which is just the hop. A forwarded browser is optional (older links
 * lack it); this page's own class stands in.
 */
function readForwardedLanding(params: URLSearchParams): string | null {
  const landing = sanitizeLandingUrl(params.get(FORWARDED_LANDING_PARAM));
  if (!landing) return null;
  if (!isOwnHost(new URL(landing).hostname)) return null;
  return landing;
}

function readFirstVisit(): FirstVisit {
  const params = new URLSearchParams(window.location.search);
  const forwarded = readForwardedLanding(params);
  if (forwarded) {
    return {
      landing: forwarded,
      referrer: sanitizeReferrer(params.get(FORWARDED_REFERRER_PARAM)),
      browser: sanitizeBrowserClass(params.get(FORWARDED_BROWSER_PARAM)) ?? currentBrowserClass(),
    };
  }
  return {
    landing: sanitizeLandingUrl(stripUrl(new URL(window.location.href))),
    referrer: readDocumentReferrer(),
    browser: currentBrowserClass(),
  };
}

/**
 * Record the first visit — landing page, referrer and browser class — set-once, as one
 * unit: all three describe the same page view, so none is written once the landing exists.
 * Strips before validating, so a long junk query string cannot block the write.
 */
function captureLandingUrl(): void {
  if (!isBrowser()) return;
  try {
    if (localStorage.getItem(LANDING_URL_KEY)) return;
    const { landing, referrer, browser } = readFirstVisit();
    if (!landing) return;
    localStorage.setItem(LANDING_URL_KEY, landing);
    if (referrer) localStorage.setItem(REFERRER_KEY, referrer);
    if (browser) localStorage.setItem(BROWSER_KEY, browser);
  } catch {
    // localStorage may be unavailable (private mode, quota); fail silent.
  }
}

/**
 * The stored first landing URL, re-validated on read, or null.
 */
export function getLandingUrl(): string | null {
  if (!isBrowser()) return null;
  try {
    return sanitizeLandingUrl(localStorage.getItem(LANDING_URL_KEY));
  } catch {
    return null;
  }
}

/** The stored first-visit referrer host, re-validated on read, or null. */
export function getFirstReferrer(): string | null {
  if (!isBrowser()) return null;
  try {
    return sanitizeReferrer(localStorage.getItem(REFERRER_KEY));
  } catch {
    return null;
  }
}

/** The stored first-visit browser class, re-validated on read, or null. */
export function getFirstVisitBrowser(): BrowserClass | null {
  if (!isBrowser()) return null;
  try {
    return sanitizeBrowserClass(localStorage.getItem(BROWSER_KEY));
  } catch {
    return null;
  }
}

function isSameMap(a: RawAttribution, b: RawAttribution): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => a[key] === b[key]);
}

export type ClerkAttributionMetadata = {
  attribution?: RawAttribution;
  firstTouch?: RawAttribution;
  landingUrl?: string;
  referrer?: string;
  firstVisitBrowser?: BrowserClass;
  signupBrowser?: BrowserClass;
  /** The `ah_vid` visitor id (town-square only), tying the account to its acquisition strategy. */
  visitorId?: string;
};

const VISITOR_ID_PATTERN = /^[a-f0-9-]{36}$/i;

/**
 * The first-party `ah_vid` visitor id town-square's middleware sets, or null. Absent on other
 * origins (the cookie is per-domain) — provisioning then falls back server-side.
 */
export function readVisitorId(): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const found = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${VISITOR_COOKIE}=`));
    const value = found ? decodeURIComponent(found.slice(VISITOR_COOKIE.length + 1)) : '';
    return VISITOR_ID_PATTERN.test(value) ? value : null;
  } catch {
    return null;
  }
}

/** Half of Clerk's ~8KB unsafeMetadata cap, leaving room for referrerHash, giftInviteToken, inviteQuestSlug. */
const MAX_METADATA_BYTES = 4096;

/**
 * Least valuable first; `attribution` is never dropped. Ranked by value, not size: the
 * small first-visit fields go before the large `firstTouch` map on purpose, because
 * `firstTouch` names the campaign and the API can fall back to `attribution` without it.
 */
const DROP_ORDER = ['signupBrowser', 'firstVisitBrowser', 'landingUrl', 'referrer', 'firstTouch'] as const;

function byteSize(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

function fitBudget(meta: ClerkAttributionMetadata): ClerkAttributionMetadata {
  return DROP_ORDER.reduce((memo, key) => {
    if (byteSize(memo) <= MAX_METADATA_BYTES) {
      return memo;
    }
    const next = { ...memo };
    delete next[key];
    return next;
  }, meta);
}

/**
 * Shape expected by Clerk `<SignUp />` / `<SignIn />` `unsafeMetadata` (and
 * OAuth transfer callbacks): last-touch `attribution`, first-touch `firstTouch`,
 * the first visit's `landingUrl`, `referrer` and `firstVisitBrowser`, and this
 * page's `signupBrowser`. Empty fields are omitted, as is a `firstTouch`
 * identical to `attribution`. Over MAX_METADATA_BYTES, fields are dropped in
 * DROP_ORDER (never `attribution`). In a browser `signupBrowser` is always
 * present; returns `undefined` only when every field is empty, so we never
 * overwrite existing metadata with an empty object.
 *
 * Every Clerk entry point must source these fields from here — never by hand.
 */
export function getClerkAttributionUnsafeMetadata(): ClerkAttributionMetadata | undefined {
  // Runs capture first, so firstTouch and landingUrl below see this page's URL.
  const attribution = readAttributionForClerk();
  const firstTouch = getFirstTouchAttribution();
  const landingUrl = getLandingUrl();
  const referrer = getFirstReferrer();
  const firstVisitBrowser = getFirstVisitBrowser();
  const signupBrowser = currentBrowserClass();
  const visitorId = readVisitorId();
  // A firstTouch identical to attribution is dropped: the API falls back to the
  // last-touch map when firstTouch is absent, and Clerk caps unsafeMetadata at ~8KB.
  const hasFirstTouch = Object.keys(firstTouch).length > 0 && !isSameMap(firstTouch, attribution);
  const meta = fitBudget({
    ...(Object.keys(attribution).length > 0 ? { attribution } : {}),
    ...(hasFirstTouch ? { firstTouch } : {}),
    ...(landingUrl ? { landingUrl } : {}),
    ...(referrer ? { referrer } : {}),
    ...(firstVisitBrowser ? { firstVisitBrowser } : {}),
    ...(signupBrowser ? { signupBrowser } : {}),
    ...(visitorId ? { visitorId } : {}),
  });
  if (Object.keys(meta).length === 0) return undefined;
  return meta;
}

export type BackfillableUser = {
  createdAt: Date | null;
  unsafeMetadata: Record<string, unknown> | null;
  update: (params: { unsafeMetadata: Record<string, unknown> }) => Promise<unknown>;
};

/** Keys only a fresh signup may receive: they describe the visit that created the account. */
const SIGNUP_KEYS = ['firstTouch', 'landingUrl', 'referrer', 'firstVisitBrowser', 'signupBrowser', 'visitorId'];

function isPresent(value: unknown): boolean {
  if (!value) return false;
  if (typeof value !== 'object') return true;
  return Object.keys(value).length > 0;
}

/**
 * How recently a Clerk user must have been created to count as a fresh signup. Only a
 * fresh signup gets the signup-only keys backfilled: a returning pre-existing account
 * would otherwise be stamped with THIS device's first visit and pay a blocking update.
 */
const NEW_USER_WINDOW_MS = 10 * 60 * 1000;

export function isFreshSignup(createdAt: Date | null): boolean {
  if (!createdAt) return false;
  return Date.now() - createdAt.getTime() < NEW_USER_WINDOW_MS;
}

function backfillKeys(createdAt: Date | null): string[] {
  if (isFreshSignup(createdAt)) {
    return ['attribution', ...SIGNUP_KEYS];
  }
  return ['attribution'];
}

/**
 * Fill attribution fields the user's unsafeMetadata lacks: `attribution` for any user,
 * the signup-only keys only for a just-created one. A guild invite token and the caller's
 * `signupExtras` (a friend's referral hash or gift token) are the same kind of signup-only
 * write — a returning account must not consume them. Never overwrites
 * a value. Clerk's `user.updated` webhook then fills the matching empty columns of the
 * user's `user_attributions` row.
 */
export async function backfillAttributionMetadata(
  user: BackfillableUser,
  inviteGuildToken: string | null = null,
  signupExtras: Record<string, string | null | undefined> = {}
) {
  const fresh = getClerkAttributionUnsafeMetadata();
  const keys = backfillKeys(user.createdAt);
  const existing = user.unsafeMetadata && typeof user.unsafeMetadata === 'object' ? user.unsafeMetadata : {};
  const missing: [string, unknown][] = fresh
    ? Object.entries(fresh).filter(([key]) => keys.includes(key) && !isPresent(existing[key]))
    : [];
  // Signup-only keys the caller supplies (a friend's `referrerHash` / `giftInviteToken`).
  const extras = { ...signupExtras, inviteGuildToken };
  Object.entries(extras).forEach(([key, value]) => {
    if (value && isFreshSignup(user.createdAt) && !isPresent(existing[key])) {
      missing.push([key, value]);
    }
  });
  if (missing.length === 0) return;

  await user.update({
    unsafeMetadata: { ...existing, ...Object.fromEntries(missing) },
  });
}

/**
 * Append last-touch attribution onto an outbound URL without clobbering any
 * params that are already on the href. Use on every cross-domain redirect.
 *
 * Merges localStorage with the current URL's query params (URL wins on key
 * conflict) so a fresh campaign on the page is not shadowed by stale
 * localStorage from a prior visit — and so callers that have not yet run
 * `captureAttributionFromUrl` still forward UTMs present on `window.location`.
 */
export function appendAttributionToUrl(href: string | URL): string {
  const merged = capKeys({ ...getRawAttribution(), ...readAttributionFromUrl() });
  const firstVisit = firstVisitParams(href);
  if (Object.keys(merged).length === 0 && Object.keys(firstVisit).length === 0) return href.toString();

  let url: URL;
  try {
    url = href instanceof URL ? new URL(href.toString()) : new URL(href, getOriginForRelative());
  } catch {
    return href.toString();
  }

  for (const [key, value] of Object.entries({ ...merged, ...firstVisit })) {
    if (!value) continue;
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  }

  if (typeof href === 'string' && isRelativeHref(href)) {
    return `${url.pathname}${url.search}${url.hash}`;
  }
  return url.toString();
}

/**
 * The stored first visit as `ah_lp` / `ah_ref` / `ah_br`, for a hop to ANOTHER origin only.
 * The landing's own origin already holds it, and a same-origin link needs nothing forwarded.
 * The landing travels exactly as stored (origin + path + attribution params), so a
 * forwarded `firstLandingPageUrl` has the same shape as a local one.
 */
function firstVisitParams(href: string | URL): Record<string, string> {
  const landing = getLandingUrl();
  if (!landing) return {};
  if (typeof href === 'string' && isRelativeHref(href)) return {};
  const target = parseUrl(href.toString());
  const landingUrl = parseUrl(landing);
  if (!target || !landingUrl || target.origin === landingUrl.origin) return {};
  return forwardedFirstVisit(landing);
}

function forwardedFirstVisit(landing: string): Record<string, string> {
  const referrer = getFirstReferrer();
  const browser = getFirstVisitBrowser();
  return {
    [FORWARDED_LANDING_PARAM]: landing,
    ...(referrer ? { [FORWARDED_REFERRER_PARAM]: referrer } : {}),
    ...(browser ? { [FORWARDED_BROWSER_PARAM]: browser } : {}),
  };
}

/**
 * Put this visitor's attribution into the address bar of the page they are on, via
 * `history.replaceState`: last-touch utm_* / click IDs plus the first visit (`ah_lp` /
 * `ah_ref` / `ah_br`), each only where the URL lacks it. Call on the sign-up and sign-in pages.
 *
 * Meta's in-app browsers keep their own storage, and Google refuses OAuth inside them,
 * so a visitor who leaves for Safari/Chrome via "Open in browser" or a copied link takes
 * only the URL with them. With this, that URL still says where they came from.
 *
 * The accepted cost: a visitor who shares their own /signup URL hands its first visit to
 * whoever opens it first in a fresh browser. Sharing an auth page's URL is rare.
 */
export function keepAttributionInAddressBar(): void {
  if (!isBrowser()) return;
  try {
    const url = new URL(window.location.href);
    const landing = getLandingUrl();
    const additions = {
      ...getRawAttribution(),
      ...(landing ? forwardedFirstVisit(landing) : {}),
    };
    const missing = Object.entries(additions).filter(([key, value]) => value && !url.searchParams.has(key));
    if (missing.length === 0) return;
    missing.forEach(([key, value]) => url.searchParams.set(key, value));
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // A blocked history or storage API leaves the URL as it was.
  }
}

function isRelativeHref(href: string): boolean {
  return !/^[a-z][a-z0-9+.-]*:/i.test(href) && !href.startsWith('//');
}

function getOriginForRelative(): string {
  if (isBrowser()) return window.location.origin;
  return 'http://localhost';
}

/**
 * Trim a payload to MAX_KEYS keys (insertion order preserved). Used after
 * merging existing-blob + URL captures so we never grow unbounded.
 */
function capKeys(input: RawAttribution): RawAttribution {
  const entries = Object.entries(input);
  if (entries.length <= MAX_KEYS) return input;
  const out: RawAttribution = {};
  for (const [k, v] of entries.slice(0, MAX_KEYS)) {
    out[k] = v;
  }
  return out;
}
